import { MemberPickerBottomSheetProvider } from '@euler/app/components/MemberPickerBottomSheet';
import {
  CustomWorkItemAlreadyExistsError,
  CustomWorkItemArgumentError,
  kCustomConstructionWorkItemCategoryId,
} from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/usePredefinedConstructionItems';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { ConstructionProcedure } from '@euler/model';
import { Assignee, ConstructionJob } from '@euler/model/task-detail';
import { array2map } from '@euler/utils/array';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import { getPyInitials } from '@euler/utils/pinyin/getPyInitials';
import produce from 'immer';
import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import { BehaviorSubject } from 'rxjs';
import {
  ConstructionWorkItemCategory,
  ConstructionWorkItemInfo,
} from './functions/useConstructionWorkItems';

export type SelectedConstructionWorkItem = {
  workItem: ConstructionWorkItemInfo;
  assignee?: Assignee;
};

type ConstructionJobScheduleContextProps = {
  selectedWorkItems$: BehaviorSubject<SelectedConstructionWorkItem[]>;
  categories$: BehaviorSubject<ConstructionWorkItemCategory[]>;
  skipJobAssignment$: BehaviorSubject<boolean>;
  addCustomWorkItem: (
    name: string,
    procedures: ConstructionProcedure[],
  ) => void;
  removeCustomWorkItem: (name: string) => void;
  clearCustomWorkItems: () => void;
};

export const ConstructionJobScheduleContext =
  createContext<ConstructionJobScheduleContextProps>(null as any);

// build the initial selected work items from default category according to
// the currently scheduled jobs.
const defaultSelectedWorkItemsFromJobs = (
  categories: ConstructionWorkItemCategory[],
  jobs: ConstructionJob[],
): SelectedConstructionWorkItem[] => {
  const defaultItems = categories.flatMap(category =>
    category.groups.flatMap(x => x.items),
  );
  const categoryMap = array2map(categories, x => x.name);
  const itemMap = array2map(defaultItems, x => x.name);
  const results: SelectedConstructionWorkItem[] = [];

  for (const job of jobs) {
    const assignee: Assignee | undefined =
      job.technicianId && job.technicianName
        ? { id: job.technicianId, name: job.technicianName }
        : undefined;
    const workItem = itemMap.get(job.name);
    if (workItem) {
      results.push({
        workItem,
        assignee,
      });
    } else {
      // custom work item.
      const category =
        job.context?.category != null
          ? categoryMap.get(job.context.category)
          : undefined;

      results.push({
        workItem: {
          name: job.name,
          categoryId: category?.id ?? kCustomConstructionWorkItemCategoryId,
          pyInitials: getPyInitials(job.name),
          details: [],
          procedures:
            job.context?.procedures.map(x => ({
              name: x.name,
              type: x.type ?? 'before',
              removable: x.removable,
              allowChooseMediaFromGallery: x.allowChooseMediaFromGallery,
            })) ??
            category?.template.procedures ??
            [],
        },
        assignee,
      });
    }
  }
  return results;
};

export const useConstructionJobScheduleContext = () => {
  return useContext(ConstructionJobScheduleContext);
};

export const useScheduleConstructionJobs = () => {
  const { selectedWorkItems$, addCustomWorkItem } =
    useConstructionJobScheduleContext();
  const [selectedWorkItems] = useBehaviorSubject(selectedWorkItems$);
  const addWorkItem = useCallback(
    (workItem: ConstructionWorkItemInfo, assignee?: Assignee) => {
      selectedWorkItems$.next([
        ...selectedWorkItems$.getValue(),
        { workItem, assignee },
      ]);
      if (workItem.categoryId === kCustomConstructionWorkItemCategoryId) {
        try {
          addCustomWorkItem(workItem.name, workItem.procedures);
        } catch (e) {
          if (e instanceof CustomWorkItemAlreadyExistsError) {
            // ignore this.
          } else if (e instanceof CustomWorkItemArgumentError) {
            // ignore this for now.
          } else {
            console.error(e);
          }
        }
      }
    },
    [addCustomWorkItem, selectedWorkItems$],
  );

  const removeWorkItem = useCallback(
    (workItem: ConstructionWorkItemInfo) => {
      const index = selectedWorkItems$
        .getValue()
        .findIndex(x => x.workItem.name === workItem.name);
      if (index < 0) {
        throw new Error('Cannot find the work item');
      }
      const nextSelectedWorkItems = selectedWorkItems$.getValue().slice();
      nextSelectedWorkItems.splice(index, 1);
      selectedWorkItems$.next(nextSelectedWorkItems);
    },
    [selectedWorkItems$],
  );

  const toggleWorkItem = useCallback(
    (workItem: ConstructionWorkItemInfo, assignee?: Assignee) => {
      const nextSelectedWorkItems = selectedWorkItems$.getValue().slice();
      const index = nextSelectedWorkItems.findIndex(
        x => x.workItem.name === workItem.name,
      );
      if (index < 0) {
        nextSelectedWorkItems.push({ workItem, assignee });
      } else {
        nextSelectedWorkItems.splice(index, 1);
      }
      selectedWorkItems$.next(nextSelectedWorkItems);
    },
    [selectedWorkItems$],
  );

  const clearWorkItems = useCallback(() => {
    selectedWorkItems$.next([]);
  }, [selectedWorkItems$]);

  const updateWorkItemsAssignee = useCallback(
    (workItems: ConstructionWorkItemInfo[], assignee: Assignee) => {
      const updated = produce(selectedWorkItems$.value, draft => {
        for (const workItem of workItems) {
          const targetWorkItem = draft.find(
            x =>
              x.workItem.name === workItem.name &&
              x.workItem.categoryId === workItem.categoryId,
          );
          if (
            targetWorkItem != null &&
            (targetWorkItem.assignee?.id !== assignee.id ||
              targetWorkItem.assignee?.name !== assignee.name)
          ) {
            targetWorkItem.assignee = assignee;
          }
        }
      });
      if (updated !== selectedWorkItems$.value) {
        selectedWorkItems$.next(updated);
      }
    },
    [selectedWorkItems$],
  );

  return {
    selectedWorkItems,
    addWorkItem,
    removeWorkItem,
    toggleWorkItem,
    clearWorkItems,
    updateWorkItemsAssignee,
  };
};

export const ConstructionJobScheduleContextProvider = memo(
  (props: {
    categories: ConstructionWorkItemCategory[];
    children?: ReactNode;
    addCustomWorkItem: (
      name: string,
      procedures: ConstructionProcedure[],
    ) => void;
    removeCustomWorkItem: (name: string) => void;
    clearCustomWorkItems: () => void;
  }) => {
    const { detail } = useTaskContext();
    const categories$ = useCreation(
      () => new BehaviorSubject<ConstructionWorkItemCategory[]>([]),
      [],
    );

    useEffect(() => {
      categories$.next(props.categories);
    }, [categories$, props.categories]);

    const [categories] = useBehaviorSubject(categories$);

    const selectedWorkItems$ = useCreation(
      () => new BehaviorSubject<SelectedConstructionWorkItem[]>([]),
      [],
    );

    const skipJobAssignment$ = useCreation(
      () => new BehaviorSubject(false),
      [],
    );

    const value = useMemo<ConstructionJobScheduleContextProps>(
      () => ({
        selectedWorkItems$,
        categories$,
        skipJobAssignment$,
        addCustomWorkItem: props.addCustomWorkItem,
        removeCustomWorkItem: props.removeCustomWorkItem,
        clearCustomWorkItems: props.clearCustomWorkItems,
      }),
      [
        selectedWorkItems$,
        categories$,
        skipJobAssignment$,
        props.addCustomWorkItem,
        props.removeCustomWorkItem,
        props.clearCustomWorkItems,
      ],
    );

    const defaultSelectedWorkItems = useMemo(
      () =>
        defaultSelectedWorkItemsFromJobs(categories, detail.construction.jobs),
      [categories, detail.construction.jobs],
    );

    const serializedDefaultSelectedWorkItems = useMemo(
      () => JSON.stringify(defaultSelectedWorkItems),
      [defaultSelectedWorkItems],
    );

    useEffect(() => {
      selectedWorkItems$.next(JSON.parse(serializedDefaultSelectedWorkItems));
    }, [selectedWorkItems$, serializedDefaultSelectedWorkItems]);

    return (
      <ConstructionJobScheduleContext.Provider value={value}>
        <MemberPickerBottomSheetProvider>
          {props.children}
        </MemberPickerBottomSheetProvider>
      </ConstructionJobScheduleContext.Provider>
    );
  },
);
